/**
 * API client for agent tasks with HITL approval workflow
 */

import { request } from "./client";

export interface AgentTask {
  id: string;
  agent: string;
  artist_id: string | null;
  organization_id: string | null;
  title: string;
  description: string | null;
  status: string;
  task_type: string;
  payload: Record<string, any> | null;
  metadata: Record<string, any> | null;
  hitl_status: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  executed_at: string | null;
  created_at: string;
  completed_at: string | null;
  kind: string;
}

export interface CreateTaskPayload {
  agent: string;
  task_type: string;
  title: string;
  description?: string;
  payload?: Record<string, any>;
  metadata?: Record<string, any>;
  kind?: string;
  auto_approve?: boolean;
}

export interface RejectTaskPayload {
  reason?: string;
}

export interface ListTasksParams {
  agent?: string;
  status?: string;
  task_type?: string;
  kind?: string;
  limit?: number;
}

/**
 * Create a new agent task
 */
export async function createTask(payload: CreateTaskPayload): Promise<AgentTask> {
  return request("/agent-tasks/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * List agent tasks with optional filters
 */
export async function listTasks(params: ListTasksParams = {}): Promise<AgentTask[]> {
  const queryParams = new URLSearchParams();
  if (params.agent) queryParams.append("agent", params.agent);
  if (params.status) queryParams.append("status", params.status);
  if (params.task_type) queryParams.append("task_type", params.task_type);
  if (params.kind) queryParams.append("kind", params.kind);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return request(`/agent-tasks/${query ? `?${query}` : ""}`);
}

/**
 * Get tasks pending approval (for HITL inbox)
 */
export async function getPendingApproval(
  agent?: string,
  limit?: number,
  kind?: string
): Promise<AgentTask[]> {
  const queryParams = new URLSearchParams();
  if (agent) queryParams.append("agent", agent);
  if (limit) queryParams.append("limit", limit.toString());
  if (kind) queryParams.append("kind", kind);

  const query = queryParams.toString();
  return request(`/agent-tasks/pending-approval${query ? `?${query}` : ""}`);
}

/**
 * Get a specific task by ID
 */
export async function getTask(taskId: string): Promise<AgentTask> {
  return request(`/agent-tasks/${taskId}`);
}

/**
 * Approve a task
 */
export async function approveTask(taskId: string): Promise<AgentTask> {
  return request(`/agent-tasks/${taskId}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/**
 * Reject a task
 */
export async function rejectTask(
  taskId: string,
  payload: RejectTaskPayload = {}
): Promise<AgentTask> {
  return request(`/agent-tasks/${taskId}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
